import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { IDefaultAPIState } from 'types/state.interface';
import type { WithOptional } from 'types/utils.type';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
  prepEntityForAPICall,
  upsertEntity,
} from 'utils/sliceHelpers';
import { type TOrganization, createOrganization } from '@timeedit/types';
import api from '../services/api.service';

export const initialState: IDefaultAPIState<TOrganization> = {
  loading: false,
  hasErrors: false,
  results: [],
  map: {},
  page: 0,
  limit: 10000,
  totalPages: 0,
};

const slice = createSlice({
  name: 'organizations',
  initialState,
  reducers: {
    fetchOrganizationRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchOrganizationSuccess: (
      state,
      { payload }: PayloadAction<TGetOrganizationResponse>
    ) => {
      const organization = createOrganization(payload);
      state.results = [organization];
      state.map[organization.id] = organization;
      finishedLoadingSuccess(state);
    },
    fetchOrganizationFailure: (state) => {
      finishedLoadingFailure(state);
    },
    saveOrganizationRequest: (state) => {
      isLoadingRequest(state);
    },
    saveOrganizationSuccess: (
      state,
      { payload }: PayloadAction<TGetOrganizationResponse>
    ) => {
      upsertEntity(state, payload, createOrganization);
      finishedLoadingSuccess(state);
    },
  },
});

export default slice.reducer;

// Selectors
export const organizationSelector = (
  state: TRootState
): TOrganization | undefined => {
  return state.organizations.map[state.auth.user?.organizationId];
};

export const organizationLoadingSelector = (state: TRootState): boolean => {
  return state.organizations.loading;
};

// Actions
export const {
  fetchOrganizationRequest,
  fetchOrganizationSuccess,
  fetchOrganizationFailure,
  saveOrganizationRequest,
  saveOrganizationSuccess,
} = slice.actions;

type TGetOrganizationResponse = WithOptional<
  TOrganization,
  | 'inactive'
  | 'inactiveSince'
  | 'enabledProducts'
  | 'type'
  | 'visibility'
  | 'rootUserPermissions'
>;

export const fetchOrganization = createAppAsyncThunk(
  'organizations/fetchOrganization',
  async (_, { dispatch, getState }) => {
    try {
      const organizationId = getState().auth?.user?.organizationId;
      if (organizationId) {
        dispatch(fetchOrganizationRequest());
        const result = (await api.get({
          endpoint: `/organizations/${organizationId}`,
        })) as TGetOrganizationResponse;
        dispatch(fetchOrganizationSuccess(result));
      } else {
        dispatch(fetchOrganizationFailure());
      }
    } catch (e) {
      dispatch(fetchOrganizationFailure());
      console.error(e);
    }
  }
);

export const saveOrganization = createAppAsyncThunk<void, TOrganization>(
  'organizations/saveOrganization',
  async (organization, { dispatch }) => {
    try {
      dispatch(saveOrganizationRequest());
      const {
        inactiveSince,
        database,
        cluster,
        region,
        customerSignature,
        ...orgBody
      } = organization;
      const preppedOrganization = prepEntityForAPICall(orgBody, false, true);
      const updatedOrganization = (await api.patch({
        endpoint: `/organizations/${organization.id}`,
        data: preppedOrganization,
      })) as TGetOrganizationResponse;
      dispatch(saveOrganizationSuccess(updatedOrganization));
    } catch (error) {
      dispatch(fetchOrganizationFailure());
      console.error(error);
    }
  }
);
